import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useIsFocused } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { LoadingAnimation, LocationSearch, NotFoundView, TransparentModal } from '../../components';
import { useIsLoggingIn } from '../../context/AuthMachine';
import { useNavigateBack } from '../../navigation';
import { GroupOrderRouteParams } from './GroupOrderSetupScreen.types';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderSearchLocationScreen = () => {
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  const isLoggingIn = useIsLoggingIn();
  const isFocused = useIsFocused();

  // ─────────────────────────────────────────────────────────────────────

  if (!isGroupOrderingEnabled) {
    return <NotFoundView />;
  }

  if (isLoggingIn) {
    return <LoadingAnimation />;
  }

  if (!isFocused) return null;

  return <GroupOrderSearchLocationScreenContent />;
};

export const GroupOrderSearchLocationScreenContent = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();

  const navigateBack = useNavigateBack();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const navigateToSetup = useCallback(
    (params: GroupOrderRouteParams) => {
      router.dismissTo({ pathname: '/group-ordering/create', params });
    },
    [router],
  );

  const navigateToLocation = useCallback<OnOpenRestaurant>(
    (context) => {
      const restaurantId = context.activeLocation?.restaurantId;

      if (!restaurantId) return;

      navigateToSetup({ restaurantId });
    },
    [navigateToSetup],
  );

  const navigateToDeliveryLocation = useCallback<OnOpenDeliveryRestaurant>(
    (context) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation?.restaurantId) return;

      navigateToSetup({
        restaurantId: deliveryLocation.restaurantId,
        deliveryAddressId: deliveryLocation.addressId,
      });
    },
    [navigateToSetup],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      accessibilityLabel={formatMessage(messages.dialogA11yLabel)}
      size="large"
      offset="10%"
      onClose={handleDismiss}
      transparent
    >
      <LocationSearch
        navigateToLocation={navigateToLocation}
        navigateToDeliveryLocation={navigateToDeliveryLocation}
        interactedLocationRestaurantSlug={undefined}
        interactedLocationDeliveryAddressId={undefined}
        isInsideModal
      />
    </TransparentModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dialogA11yLabel: {
    defaultMessage: 'Select a location',
    description: 'Group Order Setup | Location search | Dialog a11y label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchProps = ComponentProps<typeof LocationSearch>;

type OnOpenRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;
type OnOpenDeliveryRestaurant = NonNullable<LocationSearchProps['navigateToDeliveryLocation']>;
